'use client';
import { useLanguage } from '@bells/core/i18n';
import { cn } from '@bells/core/lib/utils';
import {
  attendantsAge,
  attendantTitle,
  authAttendantDescription,
  authAttendantTitle,
  authAttendantTitleSub,
  homeNewcomer,
  requestPriceOnRequest,
} from '@bells/i18n';
import { baseLocale } from '@bells/i18n/runtime';
import {
  AttendantVisibility,
  MobilityStatus,
  type AttendantSearchResponse,
} from '@bells/schema/models/attendant';
import { addMonths, isAfter } from 'date-fns';
import { first } from 'lodash';
import {
  BadgeCheck,
  Calendar,
  CameraOff,
  Car,
  Gem,
  Heart,
  MapPin,
  Ruler,
  Timer,
  Video,
} from 'lucide-react';
import NextImage from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';
import { Logo } from '../icons/logo';
import { AspectRatio } from './aspect-ratio';
import { FlagIcon } from './flag-icon';
import { Skeleton } from './skeleton';

const AttendantItem = ({
  item,
  preLoad,
}: {
  item: AttendantSearchResponse;
  preLoad?: boolean;
}) => {
  const { language } = useLanguage();
  const pickLocation = useMemo(() => {
    return first(item.locations);
  }, [item.locations]);

  const isNew = useMemo(() => {
    if (!item.created_at) {
      return false;
    }
    const oneMonthAgo = addMonths(new Date(), -1);
    return isAfter(new Date(item.created_at), oneMonthAgo);
  }, [item.created_at]);

  const currentImage = item.defaultMedia ?? null;

  const hasCar = useMemo(() => {
    return item.mobilityStatus?.includes(MobilityStatus.CAR) ?? false;
  }, [item.mobilityStatus]);

  const url = useMemo(() => {
    return language == baseLocale
      ? `/escort-dame/${item.slug}`
      : `/${language}/escort-girl/${item.slug}`;
  }, [language, item.slug]);

  return (
    <article className={cn('group/parent flex h-full w-full flex-col gap-2')}>
      <AspectRatio
        asChild
        ratio={2 / 3}
        className='group relative overflow-hidden rounded-xl'
      >
        <Link href={url} prefetch={false}>
          <figure className='relative h-full w-full duration-300 group-hover:scale-110'>
            {currentImage ? (
              <NextImage
                placeholder={currentImage.blurThumbnail ? 'blur' : undefined}
                blurDataURL={currentImage.blurThumbnail ?? undefined}
                src={currentImage.lg}
                alt={
                  pickLocation
                    ? attendantTitle({
                        name: item.name,
                        city: pickLocation.name,
                      })
                    : item.name
                }
                fill
                loading={preLoad ? 'eager' : 'lazy'}
                priority={preLoad}
                unoptimized
                className={cn('h-full w-full object-cover transition-all')}
              />
            ) : (
              <Skeleton
                className={cn('absolute left-0 top-0 h-full w-full')}
              ></Skeleton>
            )}

            {!currentImage && (
              <div className='absolute left-0 top-0 flex h-full w-full items-center justify-center bg-muted text-muted-foreground'>
                <CameraOff></CameraOff>
              </div>
            )}
          </figure>

          <div className='z-2 absolute left-4 top-4 flex flex-nowrap items-center gap-2 lg:gap-3'>
            {hasCar && (
              <div className='inline-flex h-7 w-7 items-center justify-center rounded-full bg-white text-black'>
                <Car aria-hidden='true' className='h-[70%] w-[70%]'></Car>
              </div>
            )}
            {item.hasVideos != null && item.hasVideos > 0 && (
              <div className='inline-flex h-7 w-7 items-center justify-center rounded-full bg-white text-black'>
                <Video aria-hidden='true' className='h-[70%] w-[70%]'></Video>
              </div>
            )}

            {item.visibility == AttendantVisibility.PRIVATE && (
              <div className='inline-flex items-center justify-center rounded-full text-sm text-white'>
                <Gem
                  fill='rgb(236 72 153 / var(--tw-text-opacity, 1))'
                  className='text-brand mr-2 h-4 w-4'
                ></Gem>
                <span className='text-shadow'>VIP</span>
              </div>
            )}
          </div>

          <div className='z-2 absolute right-4 top-4 flex flex-nowrap items-center gap-2 lg:gap-3'>
            <div className='inline-flex items-center justify-center rounded-full text-sm text-white'>
              <Heart
                aria-label={`Likes: ${item.totalLikes}`}
                fill='rgb(236 72 153 / var(--tw-text-opacity, 1))'
                className='mr-2 h-4 w-4 text-pink-500'
              ></Heart>
              <span className='text-shadow'>{item.totalLikes}</span>
            </div>
          </div>

          <div className='z-2 absolute bottom-4 left-4 right-4 flex items-center justify-between gap-2 lg:gap-3'>
            <div className='flex items-center gap-2'>
              {item.avgResponseTime > 0.0 && (
                <div className='inline-flex items-center gap-1 whitespace-nowrap break-keep rounded-lg bg-white px-2 py-1 text-xs font-medium uppercase text-black'>
                  <Timer className='inline-block h-4 w-3'></Timer>
                  {`${item.avgResponseTime} min`}
                </div>
              )}

              {item.distance !== null && item.distance !== undefined && (
                <div className='inline-flex items-center gap-1 whitespace-nowrap break-keep rounded-lg bg-black px-2 py-1 text-xs font-medium uppercase text-white'>
                  <MapPin className='inline-block h-4 w-3'></MapPin>
                  {`${item.distance} km`}
                </div>
              )}

              <div className='inline-flex items-center gap-1 whitespace-nowrap break-keep rounded-lg bg-black px-2 py-1 text-xs font-medium uppercase text-white'>
                {requestPriceOnRequest()}
              </div>
            </div>
          </div>
        </Link>
      </AspectRatio>
      <div className='mt-1 flex w-full flex-col gap-2'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            {isNew && (
              <span className='bg-brand text-brand-foreground break-keep px-2 py-0 text-sm uppercase'>
                {homeNewcomer()}
              </span>
            )}
            <Link
              prefetch={false}
              className='font-source line-clamp-1 w-full text-xl font-medium hover:underline xl:text-2xl'
              href={url}
            >
              {item.name}
            </Link>
          </div>
          <BadgeCheck
            fill='#377DFF'
            stroke='hsl(var(--background))'
          ></BadgeCheck>
        </div>
        <div className='flex w-full min-w-0 flex-nowrap items-center justify-between'>
          {pickLocation && (
            <span className='line-clamp-1 text-sm'>
              <FlagIcon
                className='mr-1 inline-block h-4 w-4'
                countryCode={pickLocation.countryCode.toUpperCase()}
              ></FlagIcon>

              {pickLocation.name}
            </span>
          )}

          <div className='flex flex-nowrap items-center gap-2 text-sm lg:gap-4'>
            {item.height && (
              <span className='whitespace-nowrap break-keep'>
                <Ruler className='mr-1 inline-block h-4 w-4'></Ruler>
                {`${item.height} cm`}
              </span>
            )}
            {item.age && (
              <span className='whitespace-nowrap break-keep'>
                <Calendar className='mr-1 inline-block h-4 w-4'></Calendar>
                {attendantsAge({ age: item.age ?? '' })}
              </span>
            )}
          </div>
        </div>
        <div className='line-clamp-3 w-full pt-1 text-sm text-muted-foreground'>
          {item.short_biopic}
        </div>
      </div>
    </article>
  );
};

const AttendantItemPlaceholder = () => {
  return (
    <article className={cn('group/parent flex h-full w-full flex-col gap-2')}>
      <AspectRatio
        asChild
        ratio={2 / 3}
        className='group relative overflow-hidden rounded-xl'
      >
        <figure className='relative h-full w-full duration-300 group-hover:scale-110'>
          <Skeleton
            className={cn('absolute left-0 top-0 h-full w-full')}
          ></Skeleton>
        </figure>
      </AspectRatio>
      <div className='mt-1 flex w-full flex-col gap-2'>
        <div className='flex items-center justify-between'>
          <Skeleton className='h-6 w-full'></Skeleton>
        </div>
        <div className='flex items-center justify-between'>
          <Skeleton className='h-6 w-full'></Skeleton>
        </div>
        <div className='line-clamp-3 w-full pt-1 text-sm text-muted-foreground'>
          <Skeleton className='h-6 w-full'></Skeleton>
        </div>
      </div>
    </article>
  );
};
const BecomeAttendantPlaceholder = () => {
  const { language } = useLanguage();

  const url =
    language == baseLocale ? '/escort-werden' : `/${language}/become-an-escort`;

  return (
    <article className={cn('group/parent flex h-full w-full flex-col gap-2')}>
      <AspectRatio
        asChild
        ratio={2 / 3}
        className='group relative overflow-hidden rounded-xl'
      >
        <Link href={url} prefetch={false}>
          <figure className='relative h-full w-full duration-300 group-hover:scale-110'>
            <NextImage
              src={'/become-an-escort.webp'}
              alt={authAttendantTitle()}
              fill
              unoptimized
              className={cn('h-full w-full object-cover transition-all')}
            />

            <div className='absolute left-0 top-0 z-[2] flex h-full w-full flex-col items-center justify-center gap-4 bg-background/75'>
              <Logo className='text-brand size-8'></Logo>
              <span className='text-md text-center font-bold'>
                {authAttendantTitle()}
              </span>
            </div>
          </figure>
        </Link>
      </AspectRatio>
      <div className='mt-1 flex w-full flex-col gap-2'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <Link
              prefetch={false}
              className='font-source line-clamp-1 w-full text-xl font-medium hover:underline xl:text-2xl'
              href={url}
            >
              {authAttendantTitleSub()}
            </Link>
          </div>
        </div>
        <div className='flex w-full min-w-0 flex-nowrap items-center justify-between text-muted-foreground'>
          {authAttendantDescription()}
        </div>
      </div>
    </article>
  );
};

export { AttendantItem, AttendantItemPlaceholder, BecomeAttendantPlaceholder };
